import { memo, useMemo, useRef } from 'react'
import { Content } from './Content'
import styled, { css } from 'styled-components'
import { mq, vw, designGrid, contentMobileGrid, getVariants } from '@/styles'
import { motion, useInView } from 'motion/react'
import { TypeCmsMediumImage } from '@/schema'

interface IContent {
  type: 'text' | 'empty' | 'media' | 'spacer',
  col: { from: number, expand: number },
  row: { from: number, expand: number },
  text?: string,
  media?: TypeCmsMediumImage,
  color?: string,
  align?: { v: string, h: string },
}

interface ISectionContents {
  className?: string
  section: {
    rows: number,
    columns: IContent[],
    contentType: string,
    lineBreak?: boolean
  }
  isMulticolumn?: boolean
  isThreeColumn?: boolean
  isPdP?: boolean
}

const getContentsByColumns = (columns, isMulticolumn, isThreeColumn) => columns.map((content, idx) => {
  return(
    <Content key={idx + 'column'} content={content} isMulticolumn={isMulticolumn} isThreeColumn={isThreeColumn}/>
  )
})

export const SectionContents = memo(({ className ='', section, isMulticolumn, isThreeColumn, isPdP }:ISectionContents) => {
  const { columns, contentType } = section
  const atLeastOneColIsText = useMemo(() => columns?.[0]?.type === 'text' || columns?.[1]?.type === 'text', [columns])
  const ContentsByColumns = useMemo(() => getContentsByColumns(columns, isMulticolumn, isThreeColumn), [columns, isMulticolumn, isThreeColumn])
  const ref = useRef<HTMLDivElement>(null)
	const isInView = useInView(ref, { margin: '0px', once: true })

  return (
    <Section ref={ref} initial='initial' animate='animate' exit='exit' variants={getVariants(isInView)} $contentType={contentType} $atLeastOneColIsText={atLeastOneColIsText}  $isPdP={isPdP} className={`${isMulticolumn? 'multi': ''} ${isThreeColumn? 'three_column' : ''} ${className}`} >
      {contentType === 'spacer' ? <div className='spacer'/>
        :
        ContentsByColumns
      }
    </Section>
  )
})


const Section = styled(motion.section)<{ $contentType: string, $atLeastOneColIsText: boolean, $isPdP: boolean }>`
  ${designGrid({})}
  grid-column: 1 / span 6;
  margin: ${({ $isPdP }) => $isPdP ? `${vw(15, 'mobile')} 0 ${vw(30, 'mobile')}` : `${vw(15, 'mobile')} 0`};


  ${mq.greaterThan('tablet')} {
    margin: ${({ $isPdP }) => $isPdP ? `${vw(25, 'desktop')} 0 ${vw(50, 'desktop')}` : `${vw(25, 'desktop')} 0`};
  }

  &:first-child {
    margin-top: 0;
  }

  &:last-child, &.last {
    margin-bottom: ${({ $isPdP }) => $isPdP ? `${vw(100, 'mobile')}` : '0'};

    ${mq.greaterThan('tablet')} {
      margin-bottom: ${({ $isPdP }) => $isPdP ? `${vw(150, 'desktop')}` : '0'};
    }
  }

  &.multi,
  &.three_column {
    ${contentMobileGrid({})}
    justify-content: center;

    ${mq.greaterThan('tablet')} {
      ${designGrid({})}
    }
  }

  /* &.three_column {
    @media screen and (max-width: 747px) {
      ${css`
        grid-template-columns: repeat(6, ${vw(60, 390, '%')}) !important;
      `}
    }

    ${mq.greaterThan('tablet')} {
      ${designGrid({})}
    }
  } */

  .spacer {
    grid-column: 1 / span 6;
    height: ${vw(70, 'mobile')};

    ${mq.greaterThan('tablet')} {
      grid-column: 1 / span 12;
      height: ${vw(50, 'desktop')};
    }

    ${mq.greaterThan('desktop')} {
      height: 50px;
    }
  }


  ${({ $contentType, $atLeastOneColIsText }) => ($contentType === 'two_columns' && !$atLeastOneColIsText) && css`
    article {
      &:nth-child(1) {
        ${mq.lessThan('tablet')} {
          margin-bottom: ${vw(15, 'tablet')};
        }
      }
    }
  `}
  ${({ $contentType }) => ($contentType === 'two_columns') && css`
    article {
      .medium_image {
        min-height: ${vw(230, 'mobile')};

        ${mq.greaterThan('tablet')} {
          min-height: ${vw(400, 'desktop')};
        }
      }
    }
  `}

  ${({ $contentType, $atLeastOneColIsText}) => ($contentType === 'two_columns' && $atLeastOneColIsText ) && css`
    ${mq.lessThan('tablet')} {
      margin-bottom: 0;
    }

    article {
      &.text {
        &:nth-child(2)  {
          ${mq.lessThan('tablet')} {
            padding: 0 ${vw(20, 'nexus7')};
            transform: translateY(-${vw(30, 'nexus7')})
          }

          ${mq.lessThan('nexus7')} {
            padding: 0 ${vw(20, 'mobile')};
            transform: translateY(-${vw(30, 'mobile')})
          }
        }
        &:first-child  {
          ${mq.lessThan('tablet')} {
            padding: 0 ${vw(20, 'nexus7')};
            transform: translateY(${vw(30, 'nexus7')})
          }

          ${mq.lessThan('nexus7')} {
            padding: 0 ${vw(20, 'mobile')};
            transform: translateY(${vw(30, 'mobile')})
          }
        }
      }

      &.text {
        ${mq.lessThan('tablet')} {
          z-index: 2;
        }

        > div {
          ${mq.lessThan('tablet')} {
            padding: ${vw(30, 'nexus7')} ${vw(10, 'nexus7')};
            /* background-color: var(--color-background); */
          }

          ${mq.lessThan('nexus7')} {
            padding: ${vw(30, 'mobile')} ${vw(10, 'mobile')};
          }

          ${mq.greaterThan('tablet')} {
            align-items: center;
            display: flex;
            flex-direction: column;
            justify-content: center;
          }
        }
      }
    }
  `}

  .text {
    > div {
      background-color: var(--color-background);

      ${mq.greaterThan('tablet')} {
        background-color: ${({ $isPdP }) => !$isPdP ? 'var(--color-background)' : 'transparent'};
      }
    }
  }
`
